// Cmd+K global search across Sources by name + tag values.
// Frontend-only fuzzy match over the same /api/v1/sources list endpoint.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Source } from '@datahub/shared';
import { listSources } from '../api';
import { TagChip } from '../lib/tag-colors';

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sources, setSources] = useState<Source[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const trigger = (isMac ? e.metaKey : e.ctrlKey) && e.key === 'k';
      if (trigger) {
        e.preventDefault();
        setOpen(v => !v);
        setQuery('');
        setActiveIdx(0);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listSources(200).then(setSources).catch(() => setSources([]));
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const results = useMemo(() => {
    if (!query.trim()) return sources.slice(0, 10);
    const q = query.toLowerCase();
    const scored = sources.map(s => ({
      s,
      score: rank(s, q),
    })).filter(x => x.score > 0);
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 12).map(x => x.s);
  }, [sources, query]);

  // Bound activeIdx to results length
  useEffect(() => { setActiveIdx(0); }, [query]);

  function pick(s: Source) {
    setOpen(false);
    navigate(`/sources/${s.source_id}`);
  }

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'oklch(0.18 0.01 250 / 0.4)',
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
        paddingTop: 100,
      }}
      onClick={() => setOpen(false)}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(640px, calc(100vw - 48px))',
          background: 'var(--bg-elev)', border: '1px solid var(--line)',
          borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search sources by name or tag…"
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter' && results[activeIdx]) { e.preventDefault(); pick(results[activeIdx]!); }
          }}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '14px 18px', border: 'none', outline: 'none',
            fontSize: 15, fontFamily: 'var(--ui)',
            background: 'transparent', color: 'var(--ink)',
            borderBottom: '1px solid var(--line)',
          }}
        />
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {results.length === 0 && (
            <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13, textAlign: 'center' }}>
              {query.trim() ? 'No sources match' : 'No sources yet'}
            </div>
          )}
          {results.map((s, i) => (
            <div
              key={s.source_id}
              onClick={() => pick(s)}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                padding: '10px 18px', cursor: 'pointer',
                background: i === activeIdx ? 'var(--bg-sunken)' : 'transparent',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{s.name}</div>
              <div style={{ fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span>{s.source_id}</span>
                {Object.entries(s.tags).map(([k, v]) => (
                  <TagChip key={k} k={k} v={v} compact />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: '8px 18px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', background: 'var(--bg-sunken)', display: 'flex', justifyContent: 'space-between' }}>
          <span>↑↓ navigate · ↵ open · Esc close</span>
          <span>{results.length} match{results.length === 1 ? '' : 'es'}</span>
        </div>
      </div>
    </div>
  );
}

function rank(s: Source, q: string): number {
  const name = s.name.toLowerCase();
  const id = s.source_id.toLowerCase();
  const tagsStr = Object.entries(s.tags).map(([k, v]) => `${k}=${v}`).join(' ').toLowerCase();
  let score = 0;
  if (name.includes(q)) score += name.startsWith(q) ? 100 : 50;
  if (id.includes(q)) score += 30;
  if (tagsStr.includes(q)) score += 20;
  return score;
}
